import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Search, SlidersHorizontal } from 'lucide-react';
import { AUTO_SERIES, MAX_SERIES, RankedUtmSeries } from './utils/utmSeries';

interface UtmSeriesPickerProps {
  /** Todas as origens disponíveis, já ranqueadas por volume. */
  options: RankedUtmSeries[];
  /** Origens atualmente desenhadas no gráfico. */
  selected: string[];
  onChange: (values: string[]) => void;
  /** Volta para a seleção automática (as mais relevantes por plays). */
  onResetAuto: () => void;
}

/**
 * Seletor das origens que aparecem na curva de retenção.
 *
 * Mesmo padrão visual do MetricsColumnFilter — o gráfico mostra poucas séries
 * por padrão e é aqui que o usuário troca quais quer comparar.
 */
export function UtmSeriesPicker({ options, selected, onChange, onResetAuto }: UtmSeriesPickerProps) {
  useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  // A busca roda sobre o valor cru: é assim que o usuário acha a origem pelo id
  // numérico que ele copiou do gerenciador de anúncios.
  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) => option.value.toLowerCase().includes(term));
  }, [options, search]);

  const atLimit = selected.length >= MAX_SERIES;

  const toggleSeries = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((item) => item !== value));
      return;
    }
    if (atLimit) return;
    onChange([...selected, value]);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-10 gap-2 px-3.5 text-[13px] text-[#6B7280] border-[#E5E7EB] bg-white hover:bg-white hover:text-[#6B7280] hover:border-[#E5E7EB] focus:ring-0 rounded-[10px] transition-colors cursor-pointer"
          style={{ boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.04)' }}
        >
          <SlidersHorizontal className="h-4 w-4 text-[#6B7280]" />
          {translateInterfaceText("Origens (")}{selected.length}/{options.length})
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[340px] p-0 border-[#DFE7FF] rounded-[12px] bg-white"
        align="end"
        style={{ boxShadow: '0px 12px 26px rgba(0, 0, 0, 0.06)' }}
      >
        <div className="py-3">
          {/* Header */}
          <div className="flex items-center justify-between px-4 pb-3 border-b border-[#EDF1FF]">
            <h4 className="font-semibold text-[13px] text-[#2C3345]">{translateInterfaceText("Origens no gráfico")}</h4>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2.5 text-[12px] text-[#4B7BFF] hover:bg-[#F3F6FF] hover:text-[#4B7BFF] rounded-lg transition-colors"
                onClick={onResetAuto}
              >
                {translateInterfaceText("Top")}{" "}{AUTO_SERIES}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2.5 text-[12px] text-[#6E7A95] hover:bg-[#F3F6FF] hover:text-[#475067] rounded-lg transition-colors"
                onClick={() => onChange([])}
              >
                {translateInterfaceText("Nenhuma")}</Button>
            </div>
          </div>

          {/* Busca — só faz sentido quando a lista é longa demais para varrer no olho */}
          {options.length > 12 && (
            <div className="px-4 pt-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9CA3AF]" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={translateInterfaceText("Buscar origem...")}
                  className="h-9 pl-8 text-[13px] border-[#E5E7EB] rounded-[10px] focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
            </div>
          )}

          {/* Lista de origens */}
          <div className="pt-2 max-h-[320px] overflow-y-auto metrics-scroll">
            {filteredOptions.length === 0 ? (
              <p className="px-4 py-6 text-center text-[12px] text-[#9CA3AF]">
                {translateInterfaceText("Nenhuma origem encontrada")}</p>
            ) : (
              filteredOptions.map((option) => {
                const isChecked = selected.includes(option.value);
                const isBlocked = !isChecked && atLimit;

                return (
                  <div
                    key={option.value}
                    className={`flex items-center gap-3 px-4 py-2.5 transition-all duration-150 ${
                      isBlocked
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:bg-[#F3F6FF] cursor-pointer'
                    }`}
                    onClick={() => toggleSeries(option.value)}
                  >
                    <Checkbox
                      id={`utm-series-${option.value}`}
                      checked={isChecked}
                      disabled={isBlocked}
                      onCheckedChange={() => toggleSeries(option.value)}
                      className="border-[#CBD4E4] data-[state=checked]:bg-[#4B7BFF] data-[state=checked]:border-[#4B7BFF] rounded-[4px]"
                    />
                    <Label
                      htmlFor={`utm-series-${option.value}`}
                      className={`text-[13px] font-medium text-[#475067] leading-none flex-1 min-w-0 ${
                        isBlocked ? 'cursor-not-allowed' : 'cursor-pointer'
                      }`}
                      title={option.value}
                    >
                      <span className="block truncate">{option.label}</span>
                    </Label>
                    <span className="text-[11px] text-[#9CA3AF] tabular-nums shrink-0">
                      {option.aggregated ? translateInterfaceText("agregado") : option.volume.toLocaleString(getInterfaceLanguage())}
                    </span>
                  </div>
                );
              })
            )}
          </div>

          {atLimit && (
            <p className="px-4 pt-2 text-[11px] text-[#9CA3AF]">
              {translateInterfaceText("Máximo de")}{" "}{MAX_SERIES} {" "}{translateInterfaceText("origens no gráfico. Veja todas na tabela abaixo.")}</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
